// 197 -- Event Message Board.
//
// One per-event announcement stream. Authorised staff (can_post) compose via a
// 186-pattern bottom-sheet; everyone in the audience reads. Pinned posts sort
// first, then newest. Self-contained: fetches its own posts and self-hides on
// a 403 (non-participant) so it never shouts an error onto the event page.
import { useCallback, useEffect, useState } from "react";
import type { EventPostPublic, EventPostCategory } from "@dugads/shared";
import { EVENT_POST_CATEGORIES } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Pin, PinOff, Pencil, Trash2, Plus, X, Megaphone } from "lucide-react";

const CATEGORY_LABEL: Record<EventPostCategory, string> = {
  general: "General",
  schedule_change: "Schedule change",
  logistics: "Logistics",
  urgent: "Urgent",
};

// Urgent stands out; the rest are quiet chips. Design-token classes only.
const CATEGORY_CLASS: Record<EventPostCategory, string> = {
  general: "bg-muted text-muted-foreground",
  schedule_change: "bg-primary/10 text-primary",
  logistics: "bg-primary/10 text-primary",
  urgent: "bg-destructive/10 text-destructive",
};

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function EventBoard({ eventId }: { eventId: string }) {
  const [posts, setPosts] = useState<EventPostPublic[]>([]);
  const [canPost, setCanPost] = useState(false);
  const [canPin, setCanPin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [composing, setComposing] = useState(false);
  const [editing, setEditing] = useState<EventPostPublic | null>(null);

  const reload = useCallback(() => {
    api
      .getEventPosts(eventId)
      .then((res) => {
        setPosts(res.posts);
        setCanPost(res._actions.can_post);
        setCanPin(res._actions.can_pin);
        setFailed(false);
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [eventId]);

  useEffect(() => reload(), [reload]);

  async function remove(post: EventPostPublic) {
    if (!confirm("Delete this post?")) return;
    try {
      await api.deleteEventPost(eventId, post.id);
      reload();
    } catch {
      reload();
    }
  }

  async function togglePin(post: EventPostPublic) {
    try {
      await api.updateEventPost(eventId, post.id, { pinned: !post.pinned });
      reload();
    } catch {
      reload();
    }
  }

  if (failed) return null; // non-participant -- board self-hides
  if (loading) return <div className="h-24 animate-pulse rounded-lg bg-muted" data-region="board" data-state="loading" />;

  return (
    <div className="flex flex-col gap-3" data-region="board">
      {canPost && (
        <Button size="sm" className="w-fit" data-action="new-post" onClick={() => { setEditing(null); setComposing(true); }}>
          <Plus className="mr-2 h-4 w-4" aria-hidden /> New post
        </Button>
      )}

      {posts.length === 0 ? (
        <div className="rounded-lg border border-border bg-muted/30 px-6 py-10 text-center">
          <Megaphone className="mx-auto mb-3 h-6 w-6 text-muted-foreground" aria-hidden />
          <p className="text-sm text-muted-foreground">
            No posts yet. {canPost ? "Post an update to keep everyone informed on the day." : "Event updates from the organisers will appear here."}
          </p>
        </div>
      ) : (
        posts.map((post) => (
          <Card key={post.id} data-post={post.id} data-pinned={post.pinned} data-category={post.category}>
            <CardHeader className="flex flex-row items-start justify-between gap-2 pb-2">
              <div className="flex min-w-0 flex-col gap-1">
                <div className="flex flex-wrap items-center gap-2">
                  {post.pinned && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-primary">
                      <Pin className="h-3 w-3" aria-hidden /> Pinned
                    </span>
                  )}
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${CATEGORY_CLASS[post.category]}`}>
                    {CATEGORY_LABEL[post.category]}
                  </span>
                </div>
                {post.title && <CardTitle className="text-base">{post.title}</CardTitle>}
              </div>
              {(post._actions.can_edit || post._actions.can_pin) && (
                <div className="flex shrink-0 items-center gap-0.5">
                  {post._actions.can_pin && (
                    <Button variant="ghost" size="sm" aria-label={post.pinned ? "Unpin" : "Pin"} onClick={() => togglePin(post)} data-action="toggle-pin">
                      {post.pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                    </Button>
                  )}
                  {post._actions.can_edit && (
                    <>
                      <Button variant="ghost" size="sm" aria-label="Edit post" onClick={() => { setEditing(post); setComposing(true); }} data-action="edit-post">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" aria-label="Delete post" onClick={() => remove(post)} data-action="delete-post">
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </>
                  )}
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="whitespace-pre-wrap text-sm text-foreground">{post.body}</p>
              <p className="text-xs text-muted-foreground">
                {post.author_name} ({post.author_role.replace(/_/g, " ")}) - {fmtWhen(post.created_at)}
                {post.edited_at && <span> - edited</span>}
              </p>
            </CardContent>
          </Card>
        ))
      )}

      {composing && (
        <PostComposer
          eventId={eventId}
          post={editing}
          canPin={canPin}
          onDone={() => { setComposing(false); setEditing(null); reload(); }}
          onCancel={() => { setComposing(false); setEditing(null); }}
        />
      )}
    </div>
  );
}

function PostComposer({
  eventId, post, canPin, onDone, onCancel,
}: {
  eventId: string;
  post: EventPostPublic | null;
  canPin: boolean;
  onDone: () => void;
  onCancel: () => void;
}) {
  const isEdit = post != null;
  const [title, setTitle] = useState(post?.title ?? "");
  const [body, setBody] = useState(post?.body ?? "");
  const [category, setCategory] = useState<EventPostCategory>(post?.category ?? "general");
  const [pinned, setPinned] = useState(post?.pinned ?? false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    if (!body.trim()) {
      setErr("Write something to post.");
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      if (isEdit) {
        await api.updateEventPost(eventId, post!.id, {
          title: title.trim() || null,
          body: body.trim(),
          category,
          ...(canPin ? { pinned } : {}),
        });
      } else {
        await api.createEventPost(eventId, {
          title: title.trim() || null,
          body: body.trim(),
          category,
          ...(canPin ? { pinned } : {}),
        });
      }
      onDone();
    } catch (e) {
      setErr(e instanceof api.ApiError ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  // 186 bottom-sheet on mobile, centered card on desktop.
  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center overflow-y-auto bg-black/60 p-4 sm:items-center" data-region="post-composer">
      <Card className="max-h-[90vh] w-full max-w-md overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">{isEdit ? "Edit post" : "New post"}</CardTitle>
          <Button variant="ghost" size="sm" aria-label="Close" onClick={onCancel}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col gap-1">
            <label htmlFor="post-category" className="text-xs font-medium">Category</label>
            <select
              id="post-category"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={category}
              onChange={(e) => setCategory(e.target.value as EventPostCategory)}
              data-field="category"
            >
              {EVENT_POST_CATEGORIES.map((c) => (
                <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="post-title" className="text-xs font-medium">Title (optional)</label>
            <input
              id="post-title"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-field="title"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="post-body" className="text-xs font-medium">Message</label>
            <textarea
              id="post-body"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              rows={5}
              placeholder="e.g. Gates open 08:30. B field is waterlogged -- those matches moved to A field."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              data-field="body"
            />
          </div>
          {canPin && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} data-field="pinned" />
              Pin to the top of the board
            </label>
          )}
          {err && <p className="text-sm text-destructive" data-error>{err}</p>}
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={saving} data-action="save-post">
              {isEdit ? "Save" : "Post"}
            </Button>
            <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
